"use client";

import Link from "next/link";
import type { ReactNode } from "react";

interface MarketingConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Unique id so the checkbox and its label associate correctly when more
   *  than one instance can mount on a page. */
  id?: string;
  disabled?: boolean;
  className?: string;
  /** Replaces the default "Email me seasonal ideas and offers." sentence.
   *  The unsubscribe + Privacy Policy text is always appended. */
  label?: ReactNode;
}

/**
 * Explicit, opt-in marketing-consent capture (PLA2-373 / Sys10).
 *
 * Unchecked by default. The label is short and links to the privacy policy so
 * the user knows what they are agreeing to. The boolean is surfaced to the
 * parent, which persists it (into the design resumeState) and includes it in
 * the email-send and checkout payloads alongside the email.
 */
export function MarketingConsentCheckbox({
  checked,
  onChange,
  id = "marketing-consent",
  disabled = false,
  className = "",
  label = "Email me seasonal ideas and offers.",
}: MarketingConsentCheckboxProps) {
  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-2.5 cursor-pointer select-none ${className}`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 dark:border-plantista-600 text-plantista-500 accent-plantista-500 focus:ring-2 focus:ring-plantista-500 disabled:opacity-50 disabled:cursor-not-allowed"
      />
      <span className="text-xs leading-snug text-gray-600 dark:text-plantista-300">
        {label} You can unsubscribe anytime. See our{" "}
        <Link
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-plantista-600 dark:text-plantista-400 underline hover:text-plantista-800 dark:hover:text-plantista-300"
          onClick={(e) => e.stopPropagation()}
        >
          Privacy Policy
        </Link>
        .
      </span>
    </label>
  );
}
